import { DonorWall } from '../components/DonorWall';
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, Clock, MapPin, Loader2, Check, Globe, Users, X } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SmartImage from '../components/SmartImage';
import BookingWidget from '../components/BookingWidget';
import ShareButton from '../components/ShareButton';
import { programShareText } from '../lib/shareText';
import { todRange } from '../lib/datetime';
import TrustBadge from '../components/TrustBadge';
import AddToCalendar from '../components/AddToCalendar';
import StoryShareImage from '../components/StoryShareImage';
import Lightbox from '../components/Lightbox';
import EventCommunityPhotos from '../components/EventCommunityPhotos';
import api, { mediaUrl } from '../lib/api';
import { sanitizeHtml } from '../lib/sanitize';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const fmt = (iso) => { try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); } catch { return iso; } };

const FacilitatorModal = ({ teacher, onClose }) => {
  const t = teacher;
  const bio = t.full_bio || t.short_bio;
  const socials = t.social && typeof t.social === 'object'
    ? Object.entries(t.social).filter(([, v]) => v) : [];
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="facilitator-modal">
      <div className="absolute inset-0 bg-burgundy/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-cream rounded-3xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-mahogany/70 hover:text-mahogany z-10" data-testid="facilitator-modal-close"><X className="w-5 h-5" /></button>
        <div className="p-6 sm:p-8">
          <div className="flex items-center gap-4">
            {t.photo_url
              ? <img src={mediaUrl(t.photo_url)} alt={t.name} className="w-20 h-20 rounded-full object-cover ring-2 ring-rose/40" />
              : <div className="w-20 h-20 rounded-full bg-rose-soft text-mahogany flex items-center justify-center text-xl font-medium">{t.name.split(' ').map((w) => w[0]).slice(0, 2).join('')}</div>}
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-serif-display text-2xl text-burgundy">{t.name}</h3>
                {t.is_verified && <Check className="w-4 h-4 text-green-600" title="Verified" />}
              </div>
              {t.role && <div className="text-sm text-mahogany/80 capitalize">{t.role}</div>}
              {t.years_experience ? <div className="text-xs text-mahogany/60">{t.years_experience} yrs experience</div> : null}
            </div>
          </div>

          {Array.isArray(t.specialties) && t.specialties.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {t.specialties.map((sp) => <span key={sp} className="text-xs bg-rose-soft/60 text-mahogany rounded-full px-3 py-1">{sp}</span>)}
            </div>
          )}

          {bio && <p className="text-sm text-burgundy/85 leading-relaxed mt-4 whitespace-pre-line">{bio}</p>}

          {socials.length > 0 && (
            <div className="flex flex-wrap gap-3 mt-4 text-sm">
              {socials.map(([k, v]) => (
                <a key={k} href={v} target="_blank" rel="noreferrer" className="text-rose-italic underline capitalize">{k}</a>
              ))}
            </div>
          )}

          <Link to={`/teachers/${t.slug}`} className="pill-btn justify-center w-full mt-6" data-testid="facilitator-full-profile">View full profile</Link>
        </div>
      </div>
    </div>
  );
};

const ProgramDetail = () => {
  const { slug } = useParams();
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [facilitators, setFacilitators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [booking, setBooking] = useState(false);
  const [activeTeacher, setActiveTeacher] = useState(null);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/programs/slug/${slug}`)
      .then((res) => setP(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    api.get('/teachers').then((r) => setTeachers(r.data || [])).catch((e) => console.error('Failed to load teachers:', e));
    api.get('/facilitators').then((r) => setFacilitators(r.data || [])).catch((e) => console.error('Failed to load facilitators:', e));
  }, [slug]);

  // Inject SEO meta tags for search engines & social sharing.
  useEffect(() => {
    if (!p) return;
    const setMeta = (attr, key, content) => {
      if (!content) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    const title = p.meta_title || `${p.title} | Osho Universal`;
    const desc = p.meta_description || p.short_description || '';
    const img = mediaUrl(p.og_image_url || p.featured_image_url || p.thumbnail_url);
    document.title = title;
    setMeta('name', 'description', desc);
    if (p.meta_keywords?.length) setMeta('name', 'keywords', p.meta_keywords.join(', '));
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', desc);
    setMeta('property', 'og:type', 'website');
    if (img) setMeta('property', 'og:image', img);
    setMeta('name', 'twitter:card', img ? 'summary_large_image' : 'summary');
    return () => { document.title = 'Osho Universal'; };
  }, [p]);

  if (loading) return <div className="min-h-screen bg-cream flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-mahogany" /></div>;
  if (notFound || !p) return (
    <div className="App"><Header /><div className="min-h-[50vh] bg-cream flex flex-col items-center justify-center gap-4">
      <h1 className="font-serif-display text-3xl text-burgundy">Program not found</h1>
      <Link to="/events" className="pill-btn">Browse all programs</Link>
    </div><Footer /></div>
  );

  const hero = mediaUrl(p.featured_image_url || p.thumbnail_url);
  const sym = p.currency === 'inr' ? '₹' : '$';
  const price = p.pricing_type === 'enquiry'
    ? 'Registration by phone'
    : p.pricing_type === 'donation'
    ? `Suggested donation ${sym}${p.suggested_donation}`
    : p.pricing_type === 'per_day'
    ? `${sym}${p.per_day_price} / day`
    : p.pricing_type === 'flat_or_per_day'
      ? `${sym}${p.price} full · from ${sym}${p.per_day_price} / day`
      : p.pricing_type === 'tickets'
        ? ((p.ticket_types || []).length ? `From ${sym}${Math.min(...p.ticket_types.map((t) => t.price || 0))}` : 'Tickets')
        : (p.is_free ? 'Community-Supported' : `${sym}${p.price}`);

  return (
    <div className="App">
      <Header />
      <main className="bg-cream">
        <section className="relative">
          <SmartImage src={hero} alt={p.title} className="h-[42vh] min-h-[320px] w-full" fallbackText="">
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/45 to-black/10" />
          </SmartImage>
          <div className="absolute bottom-0 left-0 right-0">
            <div className="max-w-[1100px] mx-auto px-6 lg:px-10 pb-8">
              <span className="inline-block rounded-full bg-cream/90 text-mahogany text-[11px] uppercase tracking-widest px-3 py-1 capitalize mb-3">{p.type}</span>
              <h1 className="font-serif-display text-4xl md:text-5xl text-white" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.85), 0 1px 3px rgba(0,0,0,0.9)' }}>{p.title}</h1>
            </div>
          </div>
        </section>

        <section className="max-w-[1100px] mx-auto px-6 lg:px-10 py-12 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 min-w-0 space-y-8">
            <div className="flex flex-wrap items-center gap-3" data-testid="program-share-actions">
              <ShareButton title={p.title} text={programShareText(p)} />
              <AddToCalendar title={p.title} description={p.short_description || ''} location={p.location || p.venue || ''} start={p.start_datetime} end={p.end_datetime} url={typeof window !== 'undefined' ? window.location.href : ''} />
              <StoryShareImage title={p.title} subtitle={p.start_datetime ? fmt(p.start_datetime) : (p.location || '')} coverUrl={hero} />
            </div>
            {p.short_description && <p className="text-lg text-burgundy/90">{p.short_description}</p>}
            {p.description && <div className="prose-osho text-burgundy/85 leading-relaxed break-words max-w-full" dangerouslySetInnerHTML={{ __html: sanitizeHtml(p.description) }} />}

            {p.itinerary?.length > 0 && (
              <div>
                <h2 className="font-serif-display text-2xl text-burgundy mb-4">Itinerary</h2>
                <ul className="space-y-3">
                  {p.itinerary.map((it, i) => (
                    <li key={`${it.title || it.time || 'itin'}-${i}`} className="flex gap-4 p-4 rounded-2xl bg-white/70 border border-rose/40">
                      <div className="text-mahogany font-medium min-w-[90px]">{it.label || ''}{it.time ? ` ${it.time}` : ''}</div>
                      <div><div className="text-burgundy font-medium">{it.title}</div>{it.description && <div className="text-sm text-burgundy/75 mt-1">{it.description}</div>}</div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {p.inclusions?.length > 0 && (
              <div>
                <h2 className="font-serif-display text-2xl text-burgundy mb-3">What&apos;s Included</h2>
                <ul className="grid sm:grid-cols-2 gap-2">
                  {p.inclusions.map((inc) => <li key={inc} className="flex items-start gap-2 text-burgundy/85"><Check className="w-4 h-4 text-mahogany mt-1" /> {inc}</li>)}
                </ul>
              </div>
            )}

            {p.exclusions?.length > 0 && (
              <div>
                <h2 className="font-serif-display text-2xl text-burgundy mb-3">What&apos;s Not Included</h2>
                <ul className="grid sm:grid-cols-2 gap-2">
                  {p.exclusions.map((ex) => <li key={ex} className="flex items-start gap-2 text-burgundy/85"><X className="w-4 h-4 text-red-400 mt-1" /> {ex}</li>)}
                </ul>
              </div>
            )}

            {p.what_to_bring?.length > 0 && (
              <div>
                <h2 className="font-serif-display text-2xl text-burgundy mb-3">What to Bring</h2>
                <ul className="list-disc list-inside text-burgundy/85 space-y-1">{p.what_to_bring.map((w) => <li key={w}>{w}</li>)}</ul>
              </div>
            )}

            {p.gallery_urls?.length > 0 && (
              <div>
                <h2 className="font-serif-display text-2xl text-burgundy mb-3">Gallery</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {p.gallery_urls.map((g, i) => (
                    <button key={g} type="button" onClick={() => setLightboxIdx(i)} data-testid={`gallery-thumb-${i}`}
                      className="group relative rounded-xl overflow-hidden aspect-square focus:outline-none focus:ring-2 focus:ring-mahogany">
                      <SmartImage src={mediaUrl(g)} alt="" className="rounded-xl aspect-square transition-transform duration-300 group-hover:scale-105" />
                    </button>
                  ))}
                </div>
              </div>
            )}
            {lightboxIdx !== null && (
              <Lightbox images={p.gallery_urls.map((g) => mediaUrl(g))} index={lightboxIdx} onClose={() => setLightboxIdx(null)} />
            )}

            <EventCommunityPhotos slug={p.slug} />

            {p.faqs?.length > 0 && (
              <div>
                <h2 className="font-serif-display text-2xl text-burgundy mb-3">FAQs</h2>
                <div className="space-y-3">
                  {p.faqs.map((f, i) => (
                    <details key={`${f.question || 'faq'}-${i}`} className="p-4 rounded-2xl bg-white/70 border border-rose/40">
                      <summary className="font-medium text-burgundy cursor-pointer">{f.question}</summary>
                      <p className="mt-2 text-sm text-burgundy/80">{f.answer}</p>
                    </details>
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside className="space-y-4">
            <div className="p-6 rounded-3xl bg-white/80 border border-rose/50 sticky top-24">
              <div className="font-serif-display text-3xl text-burgundy">{price}</div>
              <div className="mt-4 space-y-2 text-sm text-burgundy/85">
                {p.start_datetime && <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-rose-italic" /> {fmt(p.start_datetime)}</div>}
                {p.end_datetime && <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-rose-italic" /> to {fmt(p.end_datetime)}</div>}
                {p.type === 'class' && p.time_of_day && <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-rose-italic" /> {todRange(p.time_of_day, p.duration_minutes)}</div>}
                {p.recurrence?.frequency && p.recurrence.frequency !== 'none' && <div className="flex items-center gap-2 capitalize"><Clock className="w-4 h-4 text-rose-italic" /> {p.recurrence.frequency}{p.recurrence.days_of_week?.length ? ` (${p.recurrence.days_of_week.join(', ')})` : ''}{p.recurrence.days_of_month?.length ? ` (dates: ${p.recurrence.days_of_month.join(', ')})` : ''}{p.recurrence.until ? ` · until ${fmt(p.recurrence.until)}` : ''}</div>}
                <div className="flex items-center gap-2">{p.mode === 'online' ? <Globe className="w-4 h-4 text-rose-italic" /> : <MapPin className="w-4 h-4 text-rose-italic" />} {p.mode === 'online' ? 'Online' : (p.location || 'In person')}</div>
                {p.capacity ? <div className="flex items-center gap-2"><Users className="w-4 h-4 text-rose-italic" /> Capacity {p.capacity}</div> : null}
                {p.facilitator_names?.length > 0 && (
                  <div className="pt-1">
                    <div className="text-xs uppercase tracking-widest text-mahogany/70 mb-2">Led by</div>
                    <div className="flex flex-wrap gap-x-4 gap-y-2">
                      {p.facilitator_names.map((n) => {
                        const t = teachers.find((x) => x.name === n);
                        const fac = !t ? facilitators.find((x) => x.display_name === n) : null;
                        const photo = t?.photo_url || fac?.photo_url;
                        const initials = n.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
                        const avatar = photo
                          ? <img src={mediaUrl(photo)} alt={n} className="w-9 h-9 rounded-full object-cover ring-1 ring-rose/40" />
                          : <div className="w-9 h-9 rounded-full bg-rose-soft text-mahogany flex items-center justify-center text-xs font-medium">{initials}</div>;
                        if (t) {
                          return (
                            <button key={n} type="button" onClick={() => setActiveTeacher(t)} data-testid={`facilitator-link-${t.slug}`}
                              className="flex items-center gap-2 group">
                              {avatar}
                              <span className="text-sm text-burgundy underline decoration-rose/50 underline-offset-2 group-hover:text-rose-italic transition-colors">{n}</span>
                            </button>
                          );
                        }
                        if (fac) {
                          return (
                            <Link key={n} to={`/facilitators/${fac.id}`} data-testid={`facilitator-link-${fac.id}`}
                              className="flex items-center gap-2 group">
                              {avatar}
                              <span className="text-sm text-burgundy underline decoration-rose/50 underline-offset-2 group-hover:text-rose-italic transition-colors">{n}</span>
                            </Link>
                          );
                        }
                        return (
                          <div key={n} className="flex items-center gap-2">{avatar}<span className="text-sm text-burgundy">{n}</span></div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              {p.addons?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-rose/40">
                  <div className="text-xs uppercase tracking-widest text-mahogany/70 mb-2">Optional Add-ons</div>
                  <ul className="space-y-1.5 text-sm text-burgundy/85">
                    {p.addons.map((a) => (
                      <li key={a.id} className="flex justify-between gap-2">
                        <span>{a.name}{a.unit_label ? ` (${a.unit_label})` : ''}</span>
                        <span className="text-mahogany font-medium">{sym}{a.price}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <button onClick={() => { if (p.pricing_type !== 'enquiry' && !user) { toast({ title: 'Please sign in to book' }); navigate('/login'); } else { setBooking(true); } }}
                className="pill-btn justify-center w-full mt-5">{p.pricing_type === 'enquiry' ? 'Request to Register' : (p.is_free ? 'Register' : 'Book Now')}</button>
              {p.pricing_type !== 'donation' && <p className="text-[11px] text-mahogany/60 mt-2 text-center">Members get automatic discounts at checkout.</p>}
              <div className="mt-3 flex justify-center">
                <TrustBadge variant="light" to="/#testimonials" testid="program-trust-badge" />
              </div>
            </div>
          </aside>
        </section>
        {p.pricing_type === 'donation' && <div className="max-w-[1200px] mx-auto px-6 lg:px-10 pb-16"><DonorWall programId={p.id} title="With gratitude to those who supported this retreat" /></div>}
      </main>
      {booking && <BookingWidget program={p} onClose={() => setBooking(false)} />}
      {activeTeacher && <FacilitatorModal teacher={activeTeacher} onClose={() => setActiveTeacher(null)} />}
      <Footer />
    </div>
  );
};

export default ProgramDetail;
